import React from "react";
import clsx from "clsx";
import { TLicenseOption, TLicenseType } from "../../utils/quoteUtils";
import { formatNumber, unreachableCase } from "../../../utils";
import { UseMutateFunction } from "@tanstack/react-query";
import { QuoteCard } from "./QuoteCard";
import { QuoteCardButton } from "./QuoteCardButton";
import { QuoteCardTitle } from "./QuoteCardTitle";

interface FeatureGroup {
  groupName: string;
  features: Feature[];
}
interface Feature {
  text: string;
  plus?: boolean;
  integral?: boolean;
  comingSoon?: boolean;
}

interface LicenseQuoteCardProps {
  license: TLicenseOption;
  upgradeStatus: "upgrade" | "current" | "lateral";
  recommended?: boolean;
  setShowUpgradeForm: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedUpgrade: React.Dispatch<
    React.SetStateAction<TLicenseOption | undefined>
  >;
  downloadPDF: UseMutateFunction<
    { responseData: any; responseHeaders: any },
    unknown,
    Record<string, unknown>,
    unknown
  >;
  setQuoteStatus: React.Dispatch<
    React.SetStateAction<
      "" | "loading" | "pdf" | "json" | "error" | "renewingFromTrial"
    >
  >;
  quoteStatus: "" | "loading" | "pdf" | "json" | "error" | "renewingFromTrial";
  integralTrial: boolean;
  includeFeatures?: boolean;
  includeSparkle?: boolean;
}

const plusIntegralFeatures: FeatureGroup[] = [
  {
    groupName: "Admin Portal",
    features: [
      {
        text: "Onboarding of teachers and administrators",
        plus: true,
        integral: true,
      },
      { text: "High level teacher usage data", plus: true, integral: true },
      { text: "Student usage reports", integral: true },
      { text: "Performance reports by standard", integral: true },
      {
        text: "Pushed assessments to teachers and sections (i.e. diagnostics)",
        integral: true,
      },
    ],
  },
  {
    groupName: "Integrations",
    features: [
      { text: "Google Classroom integration", plus: true, integral: true },
      { text: "Clever or ClassLink integration", integral: true },
      { text: "Site-wide Canvas or Schoology integration", integral: true },
      {
        text: "Infinite Campus integration",
        integral: true,
        comingSoon: true,
      },
    ],
  },
  {
    groupName: "Teacher Features",
    features: [
      { text: "Embedded instructional videos", plus: true, integral: true },
      {
        text: "Automated individualized remediation",
        plus: true,
        integral: true,
      },
      { text: "Assign specific problem types", plus: true, integral: true },
      { text: "Create custom content", plus: true, integral: true },
      { text: "Print assignments to PDF", integral: true },
      { text: "Upload student work/notes", integral: true },
    ],
  },
];

function getLicenseName(type: TLicenseType): string {
  switch (type) {
    case "District License (Grades 6-12)":
      return "District Grade 6-12";
    case "District License (Grades 6-8)":
      return "District Grade 6-8";
    case "District License (Grades 7-12)":
      return "District Grade 7-12";
    case "District License (Grades 7-8)":
      return "District Grade 7-8";
    case "District License (Grades 9-12)":
      return "District Grade 9-12";
    case "School License":
    case "Individual License":
      return type;
    default:
      return unreachableCase(type);
  }
}

function getButtonText(license: TLicenseOption, upgrade = false): string {
  const licenseName = getLicenseName(license.type);
  if (!upgrade) {
    return `Download Quote for ${license.tier} ${licenseName}`;
  }
  switch (license.type) {
    case "District License (Grades 6-12)":
      return `Download Quote for Upgraded ${license.tier} District License`;
    case "District License (Grades 6-8)":
      return `Download Quote for Upgraded ${license.tier} District License`;
    case "District License (Grades 7-12)":
      return `Download Quote for Upgraded ${license.tier} District License`;
    case "District License (Grades 7-8)":
      return `Download Quote for Upgraded ${license.tier} District License`;
    case "District License (Grades 9-12)":
      return `Download Quote for Upgraded ${license.tier} District License`;
    case "School License":
    case "Individual License":
      return `Download Quote for Upgraded ${license.tier} ${licenseName}`;
    default:
      return unreachableCase(license.type);
  }
}

const LicenseQuoteCard: React.FC<LicenseQuoteCardProps> = ({
  license,
  upgradeStatus,
  recommended,
  setShowUpgradeForm,
  setSelectedUpgrade,
  downloadPDF,
  setQuoteStatus,
  quoteStatus,
  integralTrial,
  includeFeatures = false,
  includeSparkle = false,
}) => {
  const { type, tier, price, pathId, fullPrice, percentDiscount } = license;

  const showLicenseTag = recommended || upgradeStatus === "current";

  const handleCardClick = () => {
    if (integralTrial && upgradeStatus === "current") {
      setQuoteStatus("renewingFromTrial");
    }

    setShowUpgradeForm(true);
    setSelectedUpgrade(license);

    if (
      tier !== "Integral" &&
      quoteStatus !== "loading" &&
      !(integralTrial && upgradeStatus === "current")
    ) {
      const requestBody = {
        type,
        licenseTier: "Plus",
        rosteringSystem: "",
        learningManagementSystem: "",
        pathId,
      };

      setQuoteStatus("loading");
      downloadPDF(requestBody);
    }
  };

  return (
    <QuoteCard
      cardTheme={tier === "Integral" ? "highlighted" : "normal"}
      borderTheme={recommended ? "highlighted" : "normal"}
      tag={
        showLicenseTag ? (
          recommended ? (
            <>Upgraded License &ndash; Recommended</>
          ) : (
            "Current License"
          )
        ) : null
      }
      tagTheme={recommended ? "highlighted" : "normal"}
      includeSparkle={includeSparkle}
    >
      <>
        <div className="flex flex-col">
          <QuoteCardTitle tierText={tier} licenseText={getLicenseName(type)} />
          {upgradeStatus === "current" && integralTrial && (
            <p className="mt-2 text-sm">
              You currently have INTEGRAL features on a trial basis only.
            </p>
          )}

          <div className="text-bold mt-4 flex items-center sm:text-xl">
            {fullPrice && (
              <span
                className={clsx(
                  "relative mr-4 font-serif text-lg font-bold text-dm-gray-200 sm:text-xl",
                  "after:absolute after:-left-1 after:-right-1 after:top-1/2 after:h-[2px] after:-rotate-12 after:bg-dm-gray-200 after:content-['']"
                )}
              >
                ${formatNumber(fullPrice)}
              </span>
            )}

            <span className="font-serif text-lg font-bold sm:text-2xl">
              {/* SNN schools must confirm the number of students before seeing prices */}
              {price === 0 ? (
                <span className="blur">$1234</span>
              ) : (
                `$${formatNumber(price)}`
              )}
            </span>
            {percentDiscount && (
              <span className="ml-2 text-sm text-[#10862F] sm:text-lg">
                {percentDiscount}% discount
              </span>
            )}
          </div>

          <QuoteCardButton
            className="mt-6"
            theme={tier === "Integral" ? "highlighted" : "normal"}
            onClick={handleCardClick}
          >
            {getButtonText(license, recommended)}
          </QuoteCardButton>
        </div>

        {includeFeatures && (
          <ul className="flex flex-col gap-4">
            {plusIntegralFeatures.map(({ groupName, features }) => (
              <li key={groupName} className="flex flex-col gap-4">
                <h5 className="font-bold">{groupName}</h5>
                <ul className="flex flex-col gap-2">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-4">
                      <span className="flex h-6 w-6 flex-none items-center justify-center">
                        {tier === "Integral" ||
                        (tier === "Plus" && feature.plus) ? (
                          <i className="fas fa-check !text-dm-brand-blue-500" />
                        ) : (
                          <i className="fas fa-times !text-dm-gray-200" />
                        )}
                      </span>
                      <span
                        className={clsx(
                          tier === "Plus" && !feature.plus
                            ? "text-dm-gray-200 line-through"
                            : "",
                          feature.comingSoon && "italic"
                        )}
                      >
                        {feature.text}
                        {feature.comingSoon && " (coming soon!)"}
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </>
    </QuoteCard>
  );
};

export default LicenseQuoteCard;
